import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './registrationcss/Register.css';
import RegistrationProgressBar from '../../components/usercomponents/RegistrationProgressBar';
import contactDetailsImg from '../../images/ContactDetails_IMG.png';
import { db } from "../../firebase";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { userService, psgcService } from "../../services/firestoreService";
import logo from '../../images/auravit_logo.png';

// Custom Scrollable Dropdown Component
function ScrollableDropdown({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  id,
  error
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getOptionValue = (option) => option.psgc_id ?? option.id;
  const selectedOption = options.find(opt => getOptionValue(opt) === value);

  return (
    <div className="input-group select-wrapper custom-dropdown" ref={dropdownRef}>
      <label htmlFor={id}>
        {label}
      </label>
      <button
        type="button"
        className={`dropdown-button ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className={selectedOption ? '' : 'placeholder'}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading...</div>
          ) : options.length === 0 ? (
            <div className="dropdown-empty">No options available</div>
          ) : (
            <div className="dropdown-options">
              {options.map((option) => {
                const optionValue = getOptionValue(option);
                return (
                  <div
                    key={optionValue || option.name}
                    className={`dropdown-item ${value === optionValue ? 'selected' : ''}`}
                    onClick={() => {
                      onChange(optionValue);
                      setIsOpen(false);
                    }}
                  >
                    {option.name}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function ContactDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    contact: '',
    region: '',
    province: '',
    city: '',
    barangay: '',
  });

  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedBarangayId, setSelectedBarangayId] = useState('');
  const [isRegionsLoading, setIsRegionsLoading] = useState(false);
  const [isProvincesLoading, setIsProvincesLoading] = useState(false);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [isBarangaysLoading, setIsBarangaysLoading] = useState(false);

  // Saved draft names — used to restore dropdown selections after each list loads
  const draftRef = useRef({});

  // Hydrate email/contact from draft on mount; stash location names for PSGC restoration
  useEffect(() => {
    const draft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    draftRef.current = draft;

    if (draft.email || draft.contact) {
      setFormData(prev => ({
        ...prev,
        email: draft.email || '',
        contact: draft.contact || '',
        region: draft.region || '',
        province: draft.province || '',
        city: draft.city || '',
        barangay: draft.barangay || '',
      }));
    }
  }, []);

  // After regions load, find and set the saved region by name — triggers province cascade
  useEffect(() => {
    if (regions.length === 0 || !draftRef.current.region) return;
    const match = regions.find(
      r => r.name.toLowerCase() === draftRef.current.region.toLowerCase()
    );
    if (match) {
      setSelectedRegionId(String(match.psgc_id ?? match.id));
      draftRef.current = { ...draftRef.current, region: '' };
    }
  }, [regions]);

  // After provinces load, find and set the saved province by name — triggers city cascade
  useEffect(() => {
    if (provinces.length === 0 || !draftRef.current.province) return;
    const match = provinces.find(
      p => p.name.toLowerCase() === draftRef.current.province.toLowerCase()
    );
    if (match) {
      setSelectedProvinceId(String(match.psgc_id ?? match.id));
      draftRef.current = { ...draftRef.current, province: '' };
    }
  }, [provinces]);

  // After cities load, find and set the saved city by name — triggers barangay cascade
  useEffect(() => {
    if (cities.length === 0 || !draftRef.current.city) return;
    const match = cities.find(
      c => c.name.toLowerCase() === draftRef.current.city.toLowerCase()
    );
    if (match) {
      setSelectedCityId(String(match.psgc_id ?? match.id));
      draftRef.current = { ...draftRef.current, city: '' };
    }
  }, [cities]);

  // After barangays load, find and set the saved barangay by name
  useEffect(() => {
    if (barangays.length === 0 || !draftRef.current.barangay) return;
    const match = barangays.find(
      b => b.name.toLowerCase() === draftRef.current.barangay.toLowerCase()
    );
    if (match) {
      setSelectedBarangayId(String(match.psgc_id ?? match.id));
      draftRef.current = { ...draftRef.current, barangay: '' };
    }
  }, [barangays]);

  /* PSGC cascade load effects */

  // No validation cache - we only validate on submit to reduce Firestore queries

  /* ---------------- VALIDATION HELPERS ---------------- */

  // Simplified email validation that allows common email formats
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value) return 'Email is required';
        if (!validateEmail(value)) return 'Email is not a valid email';
        return '';

      case 'contact':
        if (!value) return 'Contact number is required';
        if (value.length !== 10) return 'Contact number must be exactly 10 digits';
        if (!value.startsWith('9')) return 'Mobile number must start with 9';
        return '';

      case 'region':
        if (!value) return 'Region is required';
        return '';

      case 'province':
        if (!value) return 'Province is required';
        return '';

      case 'city':
        if (!value) return 'City is required';
        return '';

      case 'barangay':
        if (!value) return 'Barangay is required';
        return '';

      default:
        return '';
    }
  };

  /* ---------------- HANDLE CHANGE ---------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'email' && value.length > 254) return;
    
    // Handle email field - remove spaces automatically
    if (name === 'email') {
      const cleanValue = value.replace(/\s/g, '');
      if (cleanValue.length > 254) return;
      
      setFormData(prev => ({ ...prev, [name]: cleanValue }));

      const err = validateField('email', cleanValue);
      setErrors(prev => {
        const updated = { ...prev };
        if (err) updated.email = err;
        else delete updated.email;
        return updated;
      });
      return;
    }

    if (name === 'contact') {
      // Only allow numbers and max 10 chars
      if (!/^\d*$/.test(value)) return; 
      if (value.length > 10) return;
    }

    setFormData((prev) => {
      if (name === 'region') {
        const region = findById(regions, value);
        return {
          ...prev,
          region: region?.name || '',
          province: '',
          city: '',
          barangay: ''
        };
      }
      if (name === 'province') {
        const province = findById(provinces, value);
        return {
          ...prev,
          province: province?.name || '',
          city: '',
          barangay: ''
        };
      }
      if (name === 'city') {
        const city = findById(cities, value);
        return {
          ...prev,
          city: city?.name || '',
          barangay: ''
        };
      }
      if (name === 'barangay') {
        const barangay = findById(barangays, value);
        return {
          ...prev,
          barangay: barangay?.name || ''
        };
      }
      return { ...prev, [name]: value };
    });

    if (name === 'region') {
      setSelectedRegionId(value);
      setSelectedProvinceId('');
      setSelectedCityId('');
      setSelectedBarangayId('');
    }
    if (name === 'province') {
      setSelectedProvinceId(value);
      setSelectedCityId('');
      setSelectedBarangayId('');
    }
    if (name === 'city') {
      setSelectedCityId(value);
      setSelectedBarangayId('');
    }
    if (name === 'barangay') {
      setSelectedBarangayId(value);
    }

    if (hasSubmitted) {
      const errorMessage = validateField(name, value);
      setErrors((prev) => {
        const next = { ...prev, [name]: errorMessage };
        if (name === 'region') {
          delete next.province;
          delete next.city;
          delete next.barangay;
        }
        if (name === 'province') {
          delete next.city;
          delete next.barangay;
        }
        if (name === 'city') {
          delete next.barangay;
        }
        return next;
      });
    }
  };

  /* ---------------- PSGC DATA HELPERS ---------------- */

  const normalizeList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  const findByName = (list, name) =>
    list.find((item) => item.name.toLowerCase() === String(name).toLowerCase());

  const findById = (list, id) =>
    list.find((item) => String(item.id || item.psgc_id) === String(id));

  useEffect(() => {
    const controller = new AbortController();
    const loadRegions = async () => {
      setIsRegionsLoading(true);
      try {
        const data = await psgcService.getRegions();
        const normalizedData = normalizeList(data);
        setRegions(normalizedData);
      } catch (error) {
        console.error("Failed to load regions:", error);
        setRegions([]);
      } finally {
        setIsRegionsLoading(false);
      }
    };

    loadRegions();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedRegionId) {
      setProvinces([]);
      return;
    }
    const controller = new AbortController();
    const loadProvinces = async () => {
      if (!selectedRegionId) return;
      setIsProvincesLoading(true);
      try {
        const data = await psgcService.getProvincesByRegion(selectedRegionId);
        setProvinces(normalizeList(data));
      } catch (error) {
        console.error("Failed to load provinces:", error);
        console.error('Error details:', error.message);
        setProvinces([]);
      } finally {
        setIsProvincesLoading(false);
      }
    };

    loadProvinces();
    return () => controller.abort();
  }, [selectedRegionId]);

  useEffect(() => {
    if (!selectedProvinceId) {
      setCities([]);
      return;
    }
    const controller = new AbortController();
    const loadCities = async () => {
      setIsCitiesLoading(true);
      try {
        const data = await psgcService.getCitiesByProvince(selectedProvinceId);
        setCities(normalizeList(data));
      } catch (error) {
        console.error("Failed to load cities:", error);
        setCities([]);
      } finally {
        setIsCitiesLoading(false);
      }
    };

    loadCities();
    return () => controller.abort();
  }, [selectedProvinceId]);

  useEffect(() => {
    if (!selectedCityId) {
      setBarangays([]);
      return;
    }
    const controller = new AbortController();
    const loadBarangays = async () => {
      setIsBarangaysLoading(true);
      try {
        const data = await psgcService.getBarangaysByCity(selectedCityId);
        setBarangays(normalizeList(data));
      } catch (error) {
        console.error("Failed to load barangays:", error);
        setBarangays([]);
      } finally {
        setIsBarangaysLoading(false);
      }
    };

    loadBarangays();
    return () => controller.abort();
  }, [selectedCityId]);

  /* ---------------- HANDLE BLUR ---------------- */

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const errorMessage = validateField(name, value);
    
    // Clear error if validation passes
    if (!errorMessage) {
      setErrors((prev) => {
        const { [name]: removed, ...rest } = prev;
        return rest;
      });
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: errorMessage,
      }));
    }
  };

  /* ---------------- FINAL VALIDATION ---------------- */

  const validate = () => {
    // Clear all errors first
    setErrors({});
    
    const newErrors = {};
    
    // Validate email and contact normally
    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const contactError = validateField('contact', formData.contact);
    if (contactError) newErrors.contact = contactError;
    
    // Validate location fields using actual field values (not IDs)
    const regionError = validateField('region', formData.region);
    if (regionError) newErrors.region = regionError;
    
    const provinceError = validateField('province', formData.province);
    if (provinceError) newErrors.province = provinceError;
    
    const cityError = validateField('city', formData.city);
    if (cityError) newErrors.city = cityError;
    
    const barangayError = validateField('barangay', formData.barangay);
    if (barangayError) newErrors.barangay = barangayError;
    
    // Set errors (will only contain fields with errors)
    setErrors(newErrors);
    
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  /**
   * Check if email is unique in database AND is a valid email
   * Backend performs deep validation (syntax + DNS + optional SMTP)
   */
  const checkEmailUniqueness = async (email) => {
    try {
      const cleaned = String(email || "").trim().toLowerCase();
      if (!validateEmail(cleaned)) {
        return { available: false, error: 'Email is not a valid email' };
      }

      const exists = await userService.checkEmailExists(cleaned);
      if (exists) {
        return { available: false, error: "This email is already registered" };
      }
      return { available: true, error: "" };
    } catch (error) {
      console.error('Email check error:', error);
      return { available: false, error: 'Unable to verify email right now' };
    }
  };

  /**
   * Check if contact number is unique in database - OPTIMIZED
   * Uses parallel queries to reduce latency while checking multiple field formats
   */
  const checkContactUniqueness = async (contact) => {
    try {
      // Extract only digits and get last 10
      const cleaned = String(contact || '').replace(/\D/g, '');
      const last10 = cleaned.slice(-10);
      
      // Check if it's exactly 10 digits starting with 9
      if (last10.length !== 10 || !last10.startsWith('9')) {
        return { available: false, error: 'Contact number must start with 9 and be exactly 10 digits' };
      }
      
      // Run all three checks in parallel to reduce latency
      // We need to check multiple fields because phone numbers are stored in different formats
      // across the database due to historical data structure variations
      const [snap1, snap2, snap3] = await Promise.all([
        getDocs(query(collection(db, "users"), where("phoneLast10", "==", last10), limit(1))),
        getDocs(query(collection(db, "users"), where("phone", "==", `+63${last10}`), limit(1))),
        getDocs(query(collection(db, "users"), where("contactDetails.contact", "==", last10), limit(1)))
      ]);
      
      // Check if any query found a match
      if (!snap1.empty || !snap2.empty || !snap3.empty) {
        return { available: false, error: "This contact number is already registered" };
      }
      
      return { available: true, error: "" };
    } catch (error) {
      console.error('Contact check error:', error);
      return { available: false, error: 'Unable to verify contact number right now' };
    }
  };

  /**
   * Save all contact details to database
   * Backend will re-validate email before saving (defense-in-depth against invalid emails)
   */
  const saveContactDetailsToDatabase = async () => {
    // Firebase-only registration: contact details are persisted when the account is created in `AccountInformation.jsx`.
    setIsSuccess(true);
    return true;
  };

  const handleContinue = async () => {
    setIsSaving(true);
    
    // 1. Validate form syntax (email format, contact format, etc.)
    setHasSubmitted(true);
    if (!validate()) {
      setIsSaving(false);
      return;
    }

    // 2. Check email: uniqueness + deep validation (syntax + DNS + optional SMTP)
    // If this fails, backend returns 400 or 409, and we show error without saving/navigating
    const emailCheck = await checkEmailUniqueness(formData.email);
    if (!emailCheck.available) {
      setErrors((prev) => ({ ...prev, email: emailCheck.error }));
      setIsSaving(false);
      return;
    }

    // 3. Check contact number uniqueness
    const contactCheck = await checkContactUniqueness(formData.contact);
    if (!contactCheck.available) {
      setErrors((prev) => ({ ...prev, contact: contactCheck.error }));
      setIsSaving(false);
      return;
    }

    // 4. Persist draft and continue
    const draft = JSON.parse(localStorage.getItem("registrationDraft") || "{}");
    const incomingState = location.state || {};
    // Get firstName and lastName from localStorage since they're not stored in Firestore
    const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    const updatedDraft = {
      ...draft,
      firstName: draft.firstName || storedDraft.firstName || incomingState.firstName || "",
      lastName: draft.lastName || storedDraft.lastName || incomingState.lastName || "",
      dob: draft.dob || incomingState.dob || "",
      sex: draft.sex || incomingState.sex || "",
      email: formData.email.trim().toLowerCase(),
      contact: formData.contact,
      phone: `+63${formData.contact}`,
      phoneLast10: formData.contact,
      region: formData.region,
      province: formData.province,
      city: formData.city,
      barangay: formData.barangay,
      registrationStep: 2,
    };
    localStorage.setItem("registrationDraft", JSON.stringify(updatedDraft));

    setIsSaving(true);
    
    // Check for assisted account flow
    const assistanceRequired = updatedDraft.assistanceRequired === 'yes';
    const reasonForAssistance = updatedDraft.reasonForAssistance || [];
    const isMinor = Array.isArray(reasonForAssistance) 
      ? reasonForAssistance.includes('minor')
      : reasonForAssistance === 'minor';
    
    // Check if user needs accompanist (PWD, Senior, NRNW, Others)
    const needsAccompanist = Array.isArray(reasonForAssistance)
      ? reasonForAssistance.some(r => ['pwd', 'senior', 'nrnw', 'others'].includes(r))
      : ['pwd', 'senior', 'nrnw', 'others'].includes(reasonForAssistance);

    setTimeout(() => {
      if (!assistanceRequired) {
        navigate('/account-setup', { state: { ...updatedDraft, isMinor: false } });
      } else if (isMinor) {
        navigate('/guardian-contact-details', { state: { ...updatedDraft, isMinor: true } });
      } else if (needsAccompanist) {
        navigate('/accompanist-contact-details', { state: updatedDraft });
      } else {
        navigate('/account-setup', { state: { ...updatedDraft, isMinor: false } });
      }
    }, 300);
  };
  /* ---------------- RENDER ---------------- */

  return (
    <>
      <div className="login-logo-container">
        <img 
          src={logo} 
          alt="AuraVit" 
          className="login-logo"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="register-container">
      <div className="register-card">
        <div className="left-panel">
          <img
            src={contactDetailsImg}
            alt="Contact details"
            className="panel-image"
          />
        </div>

        <div className="right-panel">
          <div className="registration-header-left">
            <h1 className="create-account-title">Create Account</h1>
            <p className="wellness-subtitle">
              How we can reach you safely and securely.
              <br />
              Your contact information, kept private.
            </p>
          </div>

          <RegistrationProgressBar currentStep={3} />

          {errors.submit && (
            <div className="alert alert-error" role="alert">
              {errors.submit}
            </div>
          )}

          <form noValidate>
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onKeyPress={(e) => {
                    // Prevent spacebar
                    if (e.key === ' ') {
                      e.preventDefault();
                    }
                  }}
                  placeholder="auravit@email.com"
                  className={errors.email ? 'error' : ''}
                  disabled={isSaving}
                  maxLength="254"
                  required
                />
                {errors.email && (
                  <div className="field-error">{errors.email}</div>
                )}
              </div>

              <div className="input-group">
                <label htmlFor="contact">
                  Contact Number <span className="required">*</span>
                </label>
                <div className="contact-wrapper">
                  <span className="country-code">+63</span>
                  <input
                    id="contact"
                    type="text"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    placeholder="9123456789"
                    className={errors.contact ? 'error' : ''}
                    disabled={isSaving}
                    maxLength="10"
                    inputMode="numeric"
                    required
                  />
                </div>
                {errors.contact && (
                  <div className="field-error">{errors.contact}</div>
                )}
              </div>
            </div>

            <div className="location-group">
              <span className="location-label">Location</span>
              <div className="form-row">
                <ScrollableDropdown
                  label="Region *"
                  id="region"
                  options={regions}
                  value={selectedRegionId}
                  onChange={(val) => handleChange({ target: { name: 'region', value: val } })}
                  placeholder="Select Region"
                  disabled={isSaving || isRegionsLoading}
                  loading={isRegionsLoading}
                  error={errors.region}
                />

                <ScrollableDropdown
                  label="Province *"
                  id="province"
                  options={provinces}
                  value={selectedProvinceId}
                  onChange={(val) => handleChange({ target: { name: 'province', value: val } })}
                  placeholder="Select Province"
                  disabled={isSaving || isProvincesLoading || !selectedRegionId}
                  loading={isProvincesLoading}
                  error={errors.province}
                />
              </div>

              <div className="form-row">
                <ScrollableDropdown
                  label="City/Municipality *"
                  id="city"
                  options={cities}
                  value={selectedCityId}
                  onChange={(val) => handleChange({ target: { name: 'city', value: val } })}
                  placeholder="Select City"
                  disabled={isSaving || isCitiesLoading || !selectedProvinceId}
                  loading={isCitiesLoading}
                  error={errors.city}
                />

                <ScrollableDropdown
                  label="Barangay *"
                  id="barangay"
                  options={barangays}
                  value={selectedBarangayId}
                  onChange={(val) => handleChange({ target: { name: 'barangay', value: val } })}
                  placeholder="Select Barangay"
                  disabled={isSaving || isBarangaysLoading || !selectedCityId}
                  loading={isBarangaysLoading}
                  error={errors.barangay}
                />
              </div>
            </div>

            <button
              type="button"
              className={`continue-btn ${isSuccess ? 'success-pulse' : ''}`}
              onClick={handleContinue}
              disabled={isSaving || isSuccess}
            >
              {isSaving ? "Saving..." : isSuccess ? "Saved! ✓" : "Continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
}

export default ContactDetails;
